import React from 'react';
import { useQuery } from '@tanstack/react-query';

import createContext from './support/helpers/createContext';
import { fetchJson } from './support/useFetch';

import type { AppConfig } from './core/Environment';

type Props = {
  children: React.ReactNode;
};

const [useAppConfig, Provider] = createContext<AppConfig>();

export { useAppConfig };

let appConfig: AppConfig | undefined = undefined;

// For legacy reasons, we also provide a global getter here.
export function getAppConfig() {
  if (!appConfig) {
    throw new Error(t('AppConfig is not yet initialized'));
  }
  return appConfig;
}

export function AppConfigProvider(props: Props) {
  const { data: config, error } = useQuery(
    ['app-config'],
    async () => {
      const data = await fetchJson('/api/config');
      return data.config as AppConfig;
    },
    {
      onSuccess: (config) => {
        appConfig = config;
      },
    },
  );

  if (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return (
      <>
        <p>
          {t('Error loading application configuration. Refresh to try again.')}
        </p>
        <p>{t('Error details: {errorMessage}', { errorMessage })}</p>
      </>
    );
  }
  if (config == null) {
    // Still loading
    return null;
  }
  return <Provider value={config}>{props.children}</Provider>;
}
